import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowForward, ArrowBack } from '@styled-icons/material';
import * as S from './ScrollControl.styles';

const CARD_WIDTH = 352;
const CARD_GAP = 12;
const CARDS_PER_PAGE = 3;
const DRAG_THRESHOLD_PX = 3;
const PROGRAMMATIC_SCROLL_TOLERANCE_PX = 2;
const DRAG_SPEED_MULTIPLIER = 1.5;
const WHEEL_SPEED_MULTIPLIER = 0.5;
const TRACKPAD_WHEEL_MULTIPLIER = 2;
const LINE_SCROLL_PX = 16;
const TRACKPAD_PIXEL_THRESHOLD_PX = 24;
const WHEEL_OVERRIDE_THRESHOLD_PX = 120;
const WHEEL_KIND_RESET_MS = 200;
const SCROLL_STEP_PX = CARD_WIDTH + CARD_GAP + CARDS_PER_PAGE;

type ScrollControlProps = {
  scrollItems: string[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
};

type DragState = {
  isPointerDown: boolean;
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
  suppressClick: boolean;
  pointerId: number | null;
};

type ProgrammaticScrollState = {
  isActive: boolean;
  targetIndex: number;
};

type WheelState = {
  restoreTimeoutId: number | null;
  inputKind: 'trackpad' | 'wheel' | null;
  inputKindResetId: number | null;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const ScrollControl = ({ scrollItems, scrollContainerRef }: ScrollControlProps) => {
  const [startIndex, setStartIndex] = useState(0);
  const dragStateRef = useRef<DragState>({
    isPointerDown: false,
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    suppressClick: false,
    pointerId: null,
  });
  const programmaticScrollRef = useRef<ProgrammaticScrollState>({
    isActive: false,
    targetIndex: 0,
  });
  const wheelStateRef = useRef<WheelState>({
    restoreTimeoutId: null,
    inputKind: null,
    inputKindResetId: null,
  });

  const itemCount = scrollItems.length;
  const maxIndex = Math.max(0, itemCount - CARDS_PER_PAGE);
  const inViewEndIndex = startIndex + CARDS_PER_PAGE;
  const canScrollBackward = startIndex > 0;
  const canScrollForward = startIndex + CARDS_PER_PAGE < itemCount;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const dragState = dragStateRef.current;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') {
        return;
      }
      if (event.button !== 0) {
        return;
      }

      dragState.isPointerDown = true;
      dragState.suppressClick = false;
      dragState.startX = event.clientX;
      dragState.scrollLeft = scrollContainer.scrollLeft;
      dragState.pointerId = event.pointerId;
      dragState.isDragging = false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!dragState.isPointerDown) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      if (!dragState.isDragging && Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
        dragState.isDragging = true;
        dragState.suppressClick = true;
        programmaticScrollRef.current.isActive = false;
        scrollContainer.classList.add('is-dragging');
        scrollContainer.setPointerCapture?.(event.pointerId);
      }
      if (dragState.isDragging) {
        scrollContainer.scrollLeft = dragState.scrollLeft - deltaX * DRAG_SPEED_MULTIPLIER;
      }
    };

    const endDrag = () => {
      if (!dragState.isPointerDown) {
        return;
      }

      if (dragState.isDragging) {
        dragState.isDragging = false;
        scrollContainer.classList.remove('is-dragging');
        if (dragState.pointerId !== null && scrollContainer.hasPointerCapture?.(dragState.pointerId)) {
          scrollContainer.releasePointerCapture?.(dragState.pointerId);
        }
      }

      dragState.isPointerDown = false;
      dragState.pointerId = null;

      if (dragState.suppressClick) {
        window.setTimeout(() => {
          dragState.suppressClick = false;
        }, 0);
      }
    };

    const handleClickCapture = (event: MouseEvent) => {
      if (!dragState.suppressClick) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragState.suppressClick = false;
    };

    const handleDragStart = (event: DragEvent) => {
      event.preventDefault();
    };

    scrollContainer.addEventListener('pointerdown', handlePointerDown);
    scrollContainer.addEventListener('pointermove', handlePointerMove);
    scrollContainer.addEventListener('pointerup', endDrag);
    scrollContainer.addEventListener('pointercancel', endDrag);
    scrollContainer.addEventListener('pointerleave', endDrag);
    scrollContainer.addEventListener('click', handleClickCapture, true);
    scrollContainer.addEventListener('dragstart', handleDragStart, true);

    return () => {
      scrollContainer.removeEventListener('pointerdown', handlePointerDown);
      scrollContainer.removeEventListener('pointermove', handlePointerMove);
      scrollContainer.removeEventListener('pointerup', endDrag);
      scrollContainer.removeEventListener('pointercancel', endDrag);
      scrollContainer.removeEventListener('pointerleave', endDrag);
      scrollContainer.removeEventListener('click', handleClickCapture, true);
      scrollContainer.removeEventListener('dragstart', handleDragStart, true);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const handleWheel = (event: WheelEvent) => {
      if (dragStateRef.current.isDragging) {
        return;
      }

      const { deltaX, deltaY, deltaMode, shiftKey } = event;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      const hasHorizontalIntent = absDeltaX > absDeltaY;
      const hasShiftHorizontal = shiftKey && absDeltaY > 0;

      if (!hasHorizontalIntent && !hasShiftHorizontal) {
        return;
      }

      const delta = hasShiftHorizontal && !hasHorizontalIntent ? deltaY : deltaX;
      if (delta === 0) {
        return;
      }

      const toPixels = (value: number) => {
        if (deltaMode === 1) {
          return value * LINE_SCROLL_PX;
        }
        if (deltaMode === 2) {
          return value * scrollContainer.clientWidth;
        }
        return value;
      };
      const deltaInPx = toPixels(delta);
      const maxDeltaInPx = toPixels(Math.max(absDeltaX, absDeltaY));
      const hasFractionalDelta = !Number.isInteger(deltaX) || !Number.isInteger(deltaY) || !Number.isInteger(delta);

      // Heuristic: trackpads emit small, often fractional pixel deltas; wheel mice emit larger,
      // mostly integer deltas or line/page based deltas. Lock input kind briefly to avoid jitter.
      const wheelState = wheelStateRef.current;
      if (wheelState.inputKindResetId !== null) {
        window.clearTimeout(wheelState.inputKindResetId);
      }
      wheelState.inputKindResetId = window.setTimeout(() => {
        wheelState.inputKind = null;
        wheelState.inputKindResetId = null;
      }, WHEEL_KIND_RESET_MS);

      if (!wheelState.inputKind) {
        const isLikelyTrackpad = deltaMode === 0 && (hasFractionalDelta || maxDeltaInPx < TRACKPAD_PIXEL_THRESHOLD_PX);
        wheelState.inputKind = isLikelyTrackpad ? 'trackpad' : 'wheel';
      } else if (wheelState.inputKind === 'trackpad') {
        // Large deltas or non-pixel modes indicate a wheel; switch to avoid slow, "sticky" scrolling.
        if (deltaMode !== 0 || maxDeltaInPx > WHEEL_OVERRIDE_THRESHOLD_PX) {
          wheelState.inputKind = 'wheel';
        }
      } else if (wheelState.inputKind === 'wheel') {
        // Switch back if we see tiny, fractional pixel deltas (typical trackpad signature).
        if (deltaMode === 0 && hasFractionalDelta && maxDeltaInPx < TRACKPAD_PIXEL_THRESHOLD_PX) {
          wheelState.inputKind = 'trackpad';
        }
      }

      const speedMultiplier = wheelState.inputKind === 'trackpad' ? TRACKPAD_WHEEL_MULTIPLIER : WHEEL_SPEED_MULTIPLIER;

      programmaticScrollRef.current.isActive = false;
      scrollContainer.style.scrollBehavior = 'auto';
      if (wheelStateRef.current.restoreTimeoutId !== null) {
        window.clearTimeout(wheelStateRef.current.restoreTimeoutId);
      }
      wheelStateRef.current.restoreTimeoutId = window.setTimeout(() => {
        scrollContainer.style.scrollBehavior = '';
        wheelStateRef.current.restoreTimeoutId = null;
      }, 150);
      scrollContainer.scrollLeft += deltaInPx * speedMultiplier;
      event.preventDefault();
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const handleScroll = () => {
      if (programmaticScrollRef.current.isActive) {
        const clampedTargetIndex = clamp(programmaticScrollRef.current.targetIndex, 0, maxIndex);
        programmaticScrollRef.current.targetIndex = clampedTargetIndex;

        const targetScrollLeft = clampedTargetIndex * SCROLL_STEP_PX;
        if (Math.abs(scrollContainer.scrollLeft - targetScrollLeft) > PROGRAMMATIC_SCROLL_TOLERANCE_PX) {
          return;
        }
        programmaticScrollRef.current.isActive = false;
      }

      const nextIndex = clamp(Math.round(scrollContainer.scrollLeft / SCROLL_STEP_PX), 0, maxIndex);
      setStartIndex((prevIndex) => (prevIndex === nextIndex ? prevIndex : nextIndex));
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [maxIndex, scrollContainerRef]);

  const scrollToIndex = useCallback(
    (nextIndex: number) => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        return;
      }

      const clampedIndex = clamp(nextIndex, 0, maxIndex);
      programmaticScrollRef.current.isActive = true;
      programmaticScrollRef.current.targetIndex = clampedIndex;
      scrollContainer.scrollLeft = clampedIndex * SCROLL_STEP_PX;
      setStartIndex(clampedIndex);
    },
    [maxIndex, scrollContainerRef],
  );

  const handleScrollForward = useCallback(() => {
    scrollToIndex(startIndex + CARDS_PER_PAGE);
  }, [scrollToIndex, startIndex]);

  const handleScrollBackward = useCallback(() => {
    scrollToIndex(startIndex - CARDS_PER_PAGE);
  }, [scrollToIndex, startIndex]);

  return (
    <S.ScrollContainer>
      <S.BackScrollButton
        disabled={!canScrollBackward}
        color="secondary"
        shade="light"
        onClick={handleScrollBackward}
        type="button"
        aria-label="Forrige"
      >
        <ArrowBack width={20} />
      </S.BackScrollButton>
      {scrollItems.map((key, index) => (
        <S.Dot key={`${key}_dot`} $inView={index >= startIndex && index < inViewEndIndex} />
      ))}
      <S.ForwardScrollButton
        disabled={!canScrollForward}
        color="secondary"
        shade="light"
        type="button"
        onClick={handleScrollForward}
        aria-label="Neste"
      >
        <ArrowForward width={20} />
      </S.ForwardScrollButton>
    </S.ScrollContainer>
  );
};

export default ScrollControl;
